import { useEffect, useMemo, useState } from "react";
import Branch from "components/common/Branch";
import { Form, FormElement } from "@progress/kendo-react-form";
import styles from "../Settings.module.scss";
import Nominals from "./Nominal";
import { Button } from "@progress/kendo-react-buttons";

import {
  getDefaultNominals,
  getNewDefaultNominals,
  updateDefaultNominals,
} from "api";
import { useAllNominals } from "services/nominalsService";
import { toast } from "react-toastify";
import LoadingScreen from "components/common/Loading";
import { useActiveBranches } from "services/branchService";
import SubmitButton from "components/common/SubmitButton";

const DefaultNominals = (props) => {
  const [branch, setBranch] = useState(null);
  const { data: nominalList = [] } = useAllNominals();
  const [nominalValues, setNominalValues] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { data: branchesList, loading: branchesLoading } = useActiveBranches();

  const balanceSheetOtherControlNominals = useMemo(
    () =>
      nominalList.filter(
        (item) =>
          item.type === "BalanceSheet" &&
          item.subType === "BalanceSheet_Other" &&
          item.control
      ),
    [nominalList]
  );

  const profitAndLossExpenseNominals = useMemo(
    () =>
      nominalList.filter(
        (item) =>
          item.type === "ProfitAndLoss" &&
          item.subType === "ProfitAndLoss_Expense"
      ),
    [nominalList]
  );

  const initializeBranch = () => {
    if (branchesList?.length && nominalList?.length) {
      onChangeBranch(branchesList[0], nominalList);
    }
  };

  const onChangeValue = (name, e) => {
    setNominalValues((prev) => ({
      ...prev,
      [name]: e,
    }));
  };

  const onChangeBranch = async (e, formattedData) => {
    let nominals = formattedData?.length ? formattedData : nominalList;
    if (e === null || e === undefined) {
      setBranch(null);
      setNominalValues({});
      return;
    }
    try {
      setIsLoading(true);
      const res = await getDefaultNominals(e.branchId);
      setBranch(e);
      const branchNominals = {};
      res?.data?.data?.map((item) => {
        branchNominals[item.arrayNo] = nominals.filter(
          (nominal) => nominal.nominalRef === item.nominalRef
        )[0];
      });
      setNominalValues(branchNominals);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const getNewDefaultBranch = async (branchId) => {
    try {
      const res = await getNewDefaultNominals(branchId);
      const branchNominals = {};
      res?.data?.data?.map((item) => {
        branchNominals[item.arrayNo] = {
          nominalRef: item.nominalRef,
          display: item.nominalRef,
        };
      });
      setNominalValues(branchNominals);
    } catch (error) {
      console.error("Error fetching new default branch:", error);
    }
  };

  const onSubmitChanges = async () => {
    setIsSaving(true);
    try {
      const preparedData = Object.keys(nominalValues).map((nominalValue) => {
        let item = {};
        item["arrayNo"] = nominalValue;
        item["nominalRef"] = nominalValues[nominalValue].nominalRef;
        return item;
      });
      const data = { data: preparedData };
      await updateDefaultNominals(branch.branchId, data);
      toast.success(`Default Nominals for ' ${branch.branchName} ' updated`);
    } catch (error) {
      console.log(error);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    initializeBranch();
  }, [branchesList, nominalList]);

  return (
    <div className="d-flex flex-column h-100 w-100">
      <Form
        className="d-flex flex-column flex-grow-1 overflow-hidden"
        onSubmitClick={onSubmitChanges}
        render={(formRenderProps) => {
          return (
            <FormElement className="d-flex flex-column h-100" style={{ minHeight: 0 }}>
              <div
                className="flex-grow-1 px-1 mx-auto settings-container"
                style={{ minHeight: 0, overflowY: "auto", overflowX: "hidden" }}
              >
                <div className="row">
                  <div className="col-4 ">
                    <Branch
                      branchList={branchesList}
                      value={branch}
                      onChange={onChangeBranch}
                      controlled={true}
                      name="branchId"
                      component={"DefaultNominals"}
                      width="360px"
                      labelClass="mb-2"
                      bacgroundColor="#F8F8F8"
                      loading={branchesLoading}
                    />
                  </div>
                </div>
                <hr />
                <div className="w-100 mt-2 row">
                  <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-3">
                    <Nominals
                      label="Debtors Control"
                      width="100%"
                      nominalList={balanceSheetOtherControlNominals}
                      controlled={true}
                      value={nominalValues["Debtors"]}
                      onChange={(e) => {
                        onChangeValue("Debtors", e);
                      }}
                    />
                  </div>
                  <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-3">
                    <Nominals
                      label="Office Bank"
                      width="100%"
                      nominalList={nominalList.filter(
                        (item) =>
                          item.type === "BalanceSheet" &&
                          item.subType === "BalanceSheet_OfficeBank"
                      )}
                      controlled={true}
                      value={nominalValues["OfficeBank"]}
                      onChange={(e) => {
                        onChangeValue("OfficeBank", e);
                      }}
                    />
                  </div>
                  <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-3">
                    <Nominals
                      label="Retained Profit"
                      width="100%"
                      nominalList={balanceSheetOtherControlNominals}
                      controlled={true}
                      value={nominalValues["RetainedProfit"]}
                      onChange={(e) => {
                        onChangeValue("RetainedProfit", e);
                      }}
                    />
                  </div>
                  <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-3">
                    <Nominals
                      label="Creditors Control"
                      width="100%"
                      nominalList={balanceSheetOtherControlNominals}
                      controlled={true}
                      value={nominalValues["Creditors"]}
                      onChange={(e) => {
                        onChangeValue("Creditors", e);
                      }}
                    />
                  </div>
                  <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-3">
                    <Nominals
                      label="Client Bank"
                      width="100%"
                      nominalList={nominalList.filter(
                        (item) =>
                          item.type === "BalanceSheet" &&
                          item.subType === "BalanceSheet_ClientBank"
                      )}
                      controlled={true}
                      value={nominalValues["ClientBank"]}
                      onChange={(e) => {
                        onChangeValue("ClientBank", e);
                      }}
                    />
                  </div>
                  <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-3">
                    <Nominals
                      label="Interest Paid"
                      width="100%"
                      nominalList={nominalList.filter(
                        (item) =>
                          item.type === "ProfitAndLoss" &&
                          item.subType === "ProfitAndLoss_Other"
                      )}
                      controlled={true}
                      value={nominalValues["InterestPaid"]}
                      onChange={(e) => {
                        onChangeValue("InterestPaid", e);
                      }}
                    />
                  </div>
                  <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-3">
                    <Nominals
                      label="VAT on Sale"
                      width="100%"
                      nominalList={balanceSheetOtherControlNominals}
                      controlled={true}
                      value={nominalValues["VatOnSales"]}
                      onChange={(e) => {
                        onChangeValue("VatOnSales", e);
                      }}
                    />
                  </div>
                  <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-3">
                    <Nominals
                      label="Client Control"
                      width="100%"
                      nominalList={balanceSheetOtherControlNominals}
                      controlled={true}
                      value={nominalValues["ClientControl"]}
                      onChange={(e) => {
                        onChangeValue("ClientControl", e);
                      }}
                    />
                  </div>
                  <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-3">
                    <Nominals
                      label="Profit Costs"
                      width="100%"
                      nominalList={nominalList.filter(
                        (item) =>
                          item.type === "ProfitAndLoss" &&
                          item.subType === "ProfitAndLoss_Income"
                      )}
                      controlled={true}
                      value={nominalValues["ProfitCosts"]}
                      onChange={(e) => {
                        onChangeValue("ProfitCosts", e);
                      }}
                    />
                  </div>
                  <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-3">
                    <Nominals
                      label="VAT on Purchase"
                      width="100%"
                      nominalList={balanceSheetOtherControlNominals}
                      controlled={true}
                      value={nominalValues["VatOnPurchases"]}
                      onChange={(e) => {
                        onChangeValue("VatOnPurchases", e);
                      }}
                    />
                  </div>
                  <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-3">
                    <Nominals
                      label="DDA Bank"
                      width="100%"
                      nominalList={nominalList.filter(
                        (item) =>
                          item.type === "BalanceSheet" &&
                          item.subType === "BalanceSheet_DDABank"
                      )}
                      controlled={true}
                      value={nominalValues["DDABank"]}
                      onChange={(e) => {
                        onChangeValue("DDABank", e);
                      }}
                    />
                  </div>
                  <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-3">
                    <Nominals
                      label="Unpaid Disbs Control"
                      width="100%"
                      nominalList={balanceSheetOtherControlNominals}
                      controlled={true}
                      value={nominalValues["UnpaidDisbursement"]}
                      onChange={(e) => {
                        onChangeValue("UnpaidDisbursement", e);
                      }}
                    />
                  </div>
                  <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-3">
                    <Nominals
                      label="VAT Liability"
                      width="100%"
                      nominalList={balanceSheetOtherControlNominals}
                      controlled={true}
                      value={nominalValues["VatLiability"]}
                      onChange={(e) => {
                        onChangeValue("VatLiability", e);
                      }}
                    />
                  </div>
                  <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-3">
                    <Nominals
                      label="DDA Control"
                      width="100%"
                      nominalList={balanceSheetOtherControlNominals}
                      controlled={true}
                      value={nominalValues["DDAControl"]}
                      onChange={(e) => {
                        onChangeValue("DDAControl", e);
                      }}
                    />
                  </div>
                  <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-3">
                    <Nominals
                      label="Unbilled Disbs Control"
                      width="100%"
                      nominalList={balanceSheetOtherControlNominals}
                      controlled={true}
                      value={nominalValues["UnbilledDisbursement"]}
                      onChange={(e) => {
                        onChangeValue("UnbilledDisbursement", e);
                      }}
                    />
                  </div>
                  <div className="col-xl-4 d-none d-xl-block"></div>
                  <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-3">
                    <Nominals
                      label="Bill WriteOff"
                      width="100%"
                      nominalList={profitAndLossExpenseNominals}
                      controlled={true}
                      value={nominalValues["BillWriteOff"]}
                      onChange={(e) => {
                        onChangeValue("BillWriteOff", e);
                      }}
                    />
                  </div>
                  <div className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-3">
                    <Nominals
                      label="Disbursements WriteOff"
                      width="100%"
                      nominalList={profitAndLossExpenseNominals}
                      controlled={true}
                      value={nominalValues["DisbursementWriteOff"]}
                      onChange={(e) => {
                        onChangeValue("DisbursementWriteOff", e);
                      }}
                    />
                  </div>
                </div>
              </div>
              <div
                className={`${styles["buttons-container"]} d-flex align-items-center justify-content-between px-4 py-3`}
              >
                <div>
                  {Object.keys(nominalValues).length === 0 && branch && (
                    <Button
                      className="common-btn bgBtnSecondary"
                      type="button"
                      onClick={() => getNewDefaultBranch(branch.branchId)}
                    >
                      Use Defaults
                    </Button>
                  )}
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Button className="common-btn bgBtnSecondary" type="button">
                    Cancel
                  </Button>
                  <SubmitButton
                    onClick={onSubmitChanges}
                    isLoading={isSaving}
                  >
                    Save
                  </SubmitButton>
                </div>
              </div>
            </FormElement>
          );
        }}
      />
      {isLoading && <LoadingScreen />}
    </div>
  );
};
export default DefaultNominals;
